'use client';

import { Button } from '@/ui';
import bengaliNumberFormatter from '@/utils/bn';
import { zodResolver } from '@hookform/resolvers/zod';
import { PenEditIcon, SpinnerIcon } from '@tajdid/tajdid-ui/icons';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCountdown } from 'usehooks-ts';

import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { verifyOtpSchema, VerifyOtpSchema } from '../schemas/otp.schema';
import { OtpInput } from './otp-input';

type VerifyOtpProps = {
  phoneNumber: string;
  handleSetStep: (step: number) => void;
  isPending: boolean;
  disabledOtp: boolean;
  otpError: string;
  handleOtpComplete: (otp: string) => void;
  handleResendOtp: () => void;
  otp?: string;
};

export default function VerifyOtp({
  handleSetStep,
  phoneNumber,
  isPending,
  disabledOtp,
  otpError,
  handleOtpComplete,
  handleResendOtp,
  otp,
}: VerifyOtpProps) {
  const t = useTranslations();
  const { locale } = useParams();

  const { control } = useForm<VerifyOtpSchema>({
    defaultValues: {
      code: '',
    },
    resolver: zodResolver(verifyOtpSchema),
    mode: 'onChange',
  });

  const [count, { startCountdown, resetCountdown }] = useCountdown({
    countStart: 120,
    intervalMs: 1000,
    countStop: 0,
  });

  const formatTime = (timeInSeconds: number) => {
    const date = new Date(timeInSeconds * 1000);
    const minutes = date.getUTCMinutes();
    const seconds = date.getSeconds();
    const time =
      locale === 'bn'
        ? `${bengaliNumberFormatter(minutes)}:${bengaliNumberFormatter(seconds) < bengaliNumberFormatter(10) ? '০' : ''}${bengaliNumberFormatter(seconds)}`
        : `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

    return time;
  };

  const handleResend = async () => {
    handleResendOtp();
    resetCountdown();
    startCountdown();
  };

  const handleComplete = async (otp: string) => {
    let isMounted = true;

    if (otp.length !== 4 || !phoneNumber || !isMounted) {
      return;
    }

    if (!isMounted) return; // Skip if unmounted

    handleOtpComplete(otp);

    return () => {
      isMounted = false;
    };
  };

  useEffect(() => {
    startCountdown();

    return () => {
      resetCountdown();
    };
  }, [startCountdown, resetCountdown]);

  const phone = phoneNumber;

  return (
    <form>
      <div className="container flex flex-col gap-8 items-center py-24 w-full md:max-w-md">
        <div className="text-center">
          <div className="flex gap-2 justify-center items-center">
            <h2 className="text-xl md:text-3xl font-bold text-[#333234]">
              OTP {t('opt-verification.verification')}
            </h2>
          </div>
          <p className="text-base w-[65%] mx-auto text-gray-700">
            {t('opt-verification.verification-message', { phone })}
          </p>
          {otp}
        </div>

        <Controller
          name="code"
          control={control}
          render={({ field }) => (
            <OtpInput
              value={field.value}
              name={field.name}
              onChange={value => {
                field.onChange(value);
                handleComplete(value);
              }}
              disabled={isPending || disabledOtp}
              error={otpError}
            />
          )}
        />

        {isPending && (
          <div className="flex justify-center">
            <SpinnerIcon />
          </div>
        )}

        {count > 0 ? (
          <div>
            <p className="text-base text-center text-gray-700">
              {t.rich('opt-verification.code-not-sent', {
                chunks: chunk => (
                  <>
                    <br />
                    <span>{chunk}</span>
                  </>
                ),
              })}{' '}
              <span className="text-primary-600">
                {formatTime(count)} {t('opt-verification.second')}
              </span>
            </p>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-base text-gray-700">
              {t('opt-verification.code-not-received')}
            </p>
            <Button variant="link" onClick={handleResend}>
              {t('opt-verification.resend-otp')}
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-2 p-4 w-full text-center bg-white rounded-sm border border-gray-200">
          <div className="text-base text-gray-700">
            <p>{t('opt-verification.wrong-phone-number')}</p>
            <p>{phoneNumber}</p>
          </div>

          <div className="flex gap-2 justify-center cursor-pointer text-primary-700">
            <PenEditIcon />
            <Button
              variant="link"
              onClick={() => {
                handleSetStep(1);
              }}
              className="text-base font-semibold"
            >
              {t('opt-verification.change-number')}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
